import {
    TaskColumn,
    TaskSortValue,
    TaskView,
} from "@common/types/views/tasksView";
import { NamedView, ViewDensity, ViewType } from "@common/types/views/views";
import { TaskGroupBy } from "@common/types/task";
import { TaskListQuery } from "@common/contracts/task.contract";
import { FiltersTypes } from "@common/types/filters/general";
import {
    ORGANIZATION_DEFAULT_TASK_LIST_QUERY,
    ORGANIZATION_DEFAULT_TASK_STATUSES,
} from "../taskListConstants";

export const ASSIGNED_TO_ME_VIEW_ID = "assigned-to-me";
export const CLIENT_REQUESTS_VIEW_ID = "client-requests";
export const UNASSIGNED_VIEW_ID = "unassigned";
export const WAITING_ON_CLIENT_VIEW_ID = "waiting-on-client";
export const MONTH_END_CLOSE_VIEW_ID = "month-end-close";

const ASSIGNED_TO_ME_SORT = TaskSortValue.DUE_DATE_ASC;
const ASSIGNED_TO_ME_GROUP_BY = TaskGroupBy.DUE_DATE;

export const ASSIGNED_TO_ME_TASK_LIST_QUERY: TaskListQuery = {
    ...ORGANIZATION_DEFAULT_TASK_LIST_QUERY,
    sort: ASSIGNED_TO_ME_SORT,
    groupBy: ASSIGNED_TO_ME_GROUP_BY,
};

const MONTH_END_CLOSE_SORT = TaskSortValue.DUE_DATE_ASC;
const MONTH_END_CLOSE_GROUP_BY = TaskGroupBy.CLOSE_STAGE;

export const MONTH_END_CLOSE_TASK_LIST_QUERY: TaskListQuery = {
    ...ORGANIZATION_DEFAULT_TASK_LIST_QUERY,
    sort: MONTH_END_CLOSE_SORT,
    groupBy: MONTH_END_CLOSE_GROUP_BY,
};

export const buildAssignedToMeOverlayFilters = (
    userId: number,
): FiltersTypes => ({ taskAssigneeIds: [userId] });

const buildOpenTaskView = ({
    id,
    name,
    columns,
    filters,
    groupBy,
}: {
    id: string;
    name: string;
    columns: TaskView["columns"];
    filters: TaskView["filters"];
    groupBy: TaskGroupBy;
}): NamedView<TaskView> => ({
    id,
    name,
    type: ViewType.TASK,
    columns,
    filters: {
        ...filters,
        taskStatuses: ORGANIZATION_DEFAULT_TASK_STATUSES,
    },
    options: { selection: true, density: ViewDensity.MD, groupBy },
});

/** Columns for Assigned to me; the default is the organization page's. */
export interface AssignedToMeViewOptions {
    isCloseStageShown?: boolean;
}

export function buildAssignedToMeView(
    userId: number,
    { isCloseStageShown = true }: AssignedToMeViewOptions = {},
): NamedView<TaskView> {
    return buildOpenTaskView({
        id: ASSIGNED_TO_ME_VIEW_ID,
        name: "Assigned to me",
        columns: [
            { id: TaskColumn.NAME, options: { pin: "left" } },
            { id: TaskColumn.DUE_DATE },
            { id: TaskColumn.PRIORITY },
            ...(isCloseStageShown ? [{ id: TaskColumn.CLOSE_STAGE }] : []),
            { id: TaskColumn.ACTIONS, options: { pin: "right" } },
        ],
        filters: {
            ...buildAssignedToMeOverlayFilters(userId),
            taskSort: ASSIGNED_TO_ME_SORT,
        },
        groupBy: ASSIGNED_TO_ME_GROUP_BY,
    });
}

/** Grouping and sort for the client views; the defaults are the organization page's. */
export interface ClientTaskViewOptions {
    groupBy?: TaskGroupBy;
    sort?: TaskSortValue;
}

export function buildClientRequestsView({
    groupBy = TaskGroupBy.CLIENT,
    sort = TaskSortValue.CREATED_AT_DESC,
}: ClientTaskViewOptions = {}): NamedView<TaskView> {
    return buildOpenTaskView({
        id: CLIENT_REQUESTS_VIEW_ID,
        name: "Client requests",
        columns: [
            { id: TaskColumn.NAME, options: { pin: "left" } },
            { id: TaskColumn.DUE_DATE },
            { id: TaskColumn.PRIORITY },
            { id: TaskColumn.ASSIGNEE },
            { id: TaskColumn.CREATED_AT },
            { id: TaskColumn.ACTIONS, options: { pin: "right" } },
        ],
        filters: {
            isTaskCreatedByClient: true,
            taskSort: sort,
        },
        groupBy,
    });
}

export function buildUnassignedView(): NamedView<TaskView> {
    return buildOpenTaskView({
        id: UNASSIGNED_VIEW_ID,
        name: "Unassigned",
        columns: [
            { id: TaskColumn.NAME, options: { pin: "left" } },
            { id: TaskColumn.DUE_DATE },
            { id: TaskColumn.PRIORITY },
            { id: TaskColumn.CREATED_AT },
            { id: TaskColumn.ACTIONS, options: { pin: "right" } },
        ],
        filters: {
            isTaskUnassigned: true,
            taskSort: TaskSortValue.DUE_DATE_ASC,
        },
        groupBy: TaskGroupBy.DUE_DATE,
    });
}

export function buildWaitingOnClientView({
    groupBy = TaskGroupBy.CLIENT,
    sort = TaskSortValue.DUE_DATE_ASC,
}: ClientTaskViewOptions = {}): NamedView<TaskView> {
    return buildOpenTaskView({
        id: WAITING_ON_CLIENT_VIEW_ID,
        name: "Waiting on client",
        columns: [
            { id: TaskColumn.NAME, options: { pin: "left" } },
            { id: TaskColumn.DUE_DATE },
            { id: TaskColumn.PRIORITY },
            { id: TaskColumn.ASSIGNEE },
            { id: TaskColumn.ACTIONS, options: { pin: "right" } },
        ],
        filters: {
            isTaskAssignedToClient: true,
            taskSort: sort,
        },
        groupBy,
    });
}

export function buildMonthEndCloseView(): NamedView<TaskView> {
    return buildOpenTaskView({
        id: MONTH_END_CLOSE_VIEW_ID,
        name: "Month-end close",
        columns: [
            { id: TaskColumn.NAME, options: { pin: "left" } },
            { id: TaskColumn.CLOSE },
            { id: TaskColumn.SERVICE_PERIOD },
            { id: TaskColumn.DUE_DATE },
            { id: TaskColumn.ASSIGNEE },
            { id: TaskColumn.ACTIONS, options: { pin: "right" } },
        ],
        filters: { taskSort: MONTH_END_CLOSE_SORT },
        groupBy: MONTH_END_CLOSE_GROUP_BY,
    });
}

export function buildOrganizationTaskViews(
    userId: number | undefined,
): Array<NamedView<TaskView>> {
    return [
        ...(userId === undefined ? [] : [buildAssignedToMeView(userId)]),
        buildUnassignedView(),
        buildWaitingOnClientView(),
        buildClientRequestsView(),
        buildMonthEndCloseView(),
    ];
}
